import mapboxgl from "mapbox-gl";
import { withMapboxRequestTokensMapOptions } from "#utils/mapboxToken";

const GLYPHS_OWNER = "neocmoha";
const MAPBOX_GLYPHS_STYLE_URL_PATTERN = /mapbox:\/\/fonts\/mapbox\//g;
const MAPBOX_GLYPHS_API_URL_PATTERN = /\/fonts\/v1\/mapbox\//g;
const PATCHED_MAP_MARKER = "__bipadGlyphsPatched";

export const CUSTOM_GLYPHS_URL = `mapbox://fonts/${GLYPHS_OWNER}/{fontstack}/{range}.pbf`;

type StyleInput =
  | mapboxgl.MapboxOptions["style"]
  | Parameters<mapboxgl.Map["setStyle"]>[0];

type TransformRequest = NonNullable<mapboxgl.MapboxOptions["transformRequest"]>;

type MapConstructor = typeof mapboxgl.Map & {
  [PATCHED_MAP_MARKER]?: boolean;
};

export function withCustomGlyphsUrl(url: string) {
  return url
    .replace(MAPBOX_GLYPHS_STYLE_URL_PATTERN, `mapbox://fonts/${GLYPHS_OWNER}/`)
    .replace(MAPBOX_GLYPHS_API_URL_PATTERN, `/fonts/v1/${GLYPHS_OWNER}/`);
}

export function withCustomGlyphsStyle<T extends StyleInput>(style: T): T {
  if (!style || typeof style === "string") {
    return style;
  }

  const styleObject = style as Exclude<StyleInput, string | null | undefined>;
  const glyphs = styleObject.glyphs ?? CUSTOM_GLYPHS_URL;
  const customGlyphs = withCustomGlyphsUrl(glyphs);

  if (styleObject.glyphs === customGlyphs) {
    return style;
  }

  return {
    ...styleObject,
    glyphs: customGlyphs,
  } as T;
}

export function withCustomGlyphsTransformRequest(
  transformRequest?: mapboxgl.MapboxOptions["transformRequest"]
): TransformRequest {
  return (url, resourceType) => {
    const urlWithCustomGlyphs =
      resourceType === "Glyphs" ? withCustomGlyphsUrl(url) : url;

    const request = transformRequest?.(urlWithCustomGlyphs, resourceType);

    if (request) {
      return {
        ...request,
        url:
          resourceType === "Glyphs"
            ? withCustomGlyphsUrl(request.url)
            : request.url,
      };
    }

    return { url: urlWithCustomGlyphs };
  };
}

export function withCustomGlyphsMapOptions<T extends mapboxgl.MapboxOptions>(
  options: T
): T {
  const optionsWithMapboxRequestTokens =
    withMapboxRequestTokensMapOptions(options);

  return {
    ...optionsWithMapboxRequestTokens,
    style: withCustomGlyphsStyle(optionsWithMapboxRequestTokens.style),
    transformRequest: withCustomGlyphsTransformRequest(
      optionsWithMapboxRequestTokens.transformRequest
    ),
  };
}

function configureMapboxGlyphs() {
  mapboxgl.config.GLYPHS_URL = CUSTOM_GLYPHS_URL;

  const OriginalMap = mapboxgl.Map as MapConstructor;
  if (OriginalMap[PATCHED_MAP_MARKER]) {
    return;
  }

  class MapWithCustomGlyphs extends OriginalMap {
    public constructor(options: mapboxgl.MapboxOptions) {
      super(withCustomGlyphsMapOptions(options));
    }

    public setStyle(
      style: Parameters<mapboxgl.Map["setStyle"]>[0],
      options?: Parameters<mapboxgl.Map["setStyle"]>[1]
    ) {
      return super.setStyle(withCustomGlyphsStyle(style), options);
    }
  }

  Object.defineProperty(MapWithCustomGlyphs, PATCHED_MAP_MARKER, {
    value: true,
  });

  mapboxgl.Map = MapWithCustomGlyphs as typeof mapboxgl.Map;
}

configureMapboxGlyphs();
